





































import { AconexImportDialogParams } from '@/components/documents/aconex-import-dialog/aconex-import-dialog-models';
import {
  AddEditDialogMode,
  AddEditDocumentDialogParams,
} from '@/components/documents/add-edit-document-dialog/add-edit-document-dialog-models';
import { SelectDocumentDialogParams } from '@/components/documents/select-document-dialog/select-document-dialog-models';
import OtStandardHeaderArchetype from '@/components/global/archetypes/ot-standard-header-archetype.vue';
import { IInputAction, IInputTab } from '@/components/global/common-models';
import OtButton from '@/components/global/ot-button.vue';
import OtLoadingSpinner from '@/components/global/ot-loading-spinner.vue';
import OtTabs from '@/components/global/ot-tabs.vue';
import OtTag, { TagStatus } from '@/components/global/ot-tag.vue';
import { doAconexImport } from '@/services/aconex.service';
import OtApi, { executeApi } from '@/services/api.service';
import {
  PostUpdateSegmentInstanceDocumentsRequestDocumentVersionAssociationModel,
  PostUpdateSegmentInstanceDocumentsRequestModel,
  SegmentInstanceDocumentVersionAssociationType,
} from '@/services/generated/api';
import { vxm } from '@/store';
import { OtOpenSegmentStatusSet, OtSegmentStatus, OtStatusType } from '@/types/status-enums';
import { areSame } from '@/utils/string-utils';
import { OtClientEvaluation } from '@/wf-components/models/client-evaluation';
import { OtDataDrivenDefinition } from '@/wf-components/models/data-driven-definition';
import { OtDataDrivenInstances } from '@/wf-components/models/data-driven-instance';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { SegmentConfig, SegmentInstanceDetails } from './claims-models';
import {
  ROUTE_WORKFLOW_ADD,
  ROUTE_WORKFLOW_DETAILS,
  ROUTE_WORKFLOW_DETAILS_EDIT,
  ROUTE_WORKFLOW_DOCUMENTS,
  ROUTE_WORKFLOW_VIEW,
} from './claims-routes';

@Component({
  components: {
    OtLoadingSpinner,
    OtStandardHeaderArchetype,
    OtTabs,
    OtTag,
    OtButton,
  },
})
export default class OtClaimLayout extends Vue {
  // * PROPS

  // * REFS

  // * DATA
  private get newWorkflowButton(): IInputAction {
    return {
      actionName: 'New Workflow',
      action: {
        name: ROUTE_WORKFLOW_ADD,
        query: { project: this.claimDetails.project.gid, contract: this.claimDetails.contract.gid },
      },
    };
  }
  private newDocumentButton: IInputAction = {
    actionName: 'New Document',
    action: () => this.createNewDocument(),
  };
  private importDocumentButton: IInputAction = {
    actionName: 'Add Existing Document',
    action: () => this.importDocument(),
  };
  private importFromAconexButton: IInputAction = {
    actionName: 'Import From Aconex',
    action: () => this.importFromAconex(),
  };

  private viewClaimTabs: IInputTab[] = [
    {
      tabId: 0,
      tabText: 'Workflow Details',
      tabRoute: {
        name: ROUTE_WORKFLOW_DETAILS,
      },
    },
    {
      tabId: 1,
      tabText: 'Workflow',
      tabRoute: {
        name: ROUTE_WORKFLOW_VIEW,
      },
    },
    {
      tabId: 2,
      tabText: 'Documents',
      tabRoute: {
        name: ROUTE_WORKFLOW_DOCUMENTS,
      },
    },
  ];
  // these are vestigial tabs - at one stage there was going to be the ability to click Edit. Then it got taken away
  private formClaimTabs: IInputTab[] = [
    {
      tabId: 0,
      tabText: 'Workflow Details',
      tabRoute: {
        name: ROUTE_WORKFLOW_DETAILS_EDIT,
      },
    },
  ];

  // ----- Data Driven Tab Data ----- //
  private dataDrivenSegmentDefinition: OtDataDrivenDefinition | null = null;
  private dataDrivenSegmentInstances = OtDataDrivenInstances.createEmpty();
  private evaluations: OtClientEvaluation[] = [];
  private segmentConfig: SegmentConfig | null = null;

  private api = new OtApi();
  private loading = false;
  private claimDetails = SegmentInstanceDetails.createEmpty();

  // * COMPUTED
  private get sectionTitle(): string {
    if (this.claimDetails) {
      return `${this.claimDetails.reference} - ${this.claimDetails.workflowSegment.shortName} Workflow`;
    }
    return '';
  }

  private get isContextBtnDisabled(): boolean {
    return this.isInFormState;
  }

  private get isInFormState(): boolean {
    const routeName = this.$route.name || '';
    const formRouteNames = this.formClaimTabs.map(t => t.tabRoute?.name || '');
    return formRouteNames.includes(routeName);
  }

  private get effectiveViewClaimTabs() {
    const claimTabs: IInputTab[] = [...this.viewClaimTabs];
    return claimTabs;
  }

  private get claimTabs(): IInputTab[] {
    if (this.isInFormState) {
      return this.formClaimTabs;
    }
    return this.effectiveViewClaimTabs;
  }

  private get claimGid(): string {
    return this.$route.params.claimGid;
  }

  private get currentRouteName() {
    return this.$route.name;
  }

  private get primaryContextBtn() {
    switch (this.currentRouteName) {
      case ROUTE_WORKFLOW_DOCUMENTS:
        // documents gets new document
        return this.newDocumentButton;
      default:
        // everything esle gets new workflow
        return this.newWorkflowButton;
    }
  }

  private get secondaryContextBtns() {
    switch (this.currentRouteName) {
      case ROUTE_WORKFLOW_DOCUMENTS:
        // documents gets import
        return [this.importDocumentButton, this.importFromAconexButton];
      default:
        // everything else gets nothing
        return [];
    }
  }

  // * WATCHERS
  @Watch('routePath')
  private async checkRoutePath() {
    this.loading = true;

    // Since this function will get called any time the user selects a new tab,
    // we only need to load the data if it hasn't already been loaded
    if (this.claimDetails === null || !areSame(this.claimDetails.gid, this.claimGid)) {
      const apiCalls: Promise<void>[] = [
        this.handleReloadClaimDetails(),
        this.loadSegmentDefinition(),
        this.loadSegmentResponse(),
        this.loadSegmentEvaluation(),
      ];
      await Promise.all(apiCalls);
    }

    this.loading = false;
  }

  // * METHODS
  private getTagStatus(status: OtSegmentStatus): TagStatus {
    return { type: OtStatusType.Segment, status };
  }

  private async handleReloadClaimDetails(callback?: () => void) {
    if (this.claimGid) {
      const result = await executeApi(
        () => this.api.segmentInstances().getSegmentInstanceDetails(this.claimGid),
        'Load Workflow Details',
      );
      if (result.success && result.data) {
        this.claimDetails = SegmentInstanceDetails.createFromApiResponse(result.data);
        if (OtOpenSegmentStatusSet.has(this.claimDetails.status)) {
          // it's an open claim. Bounce them to edit it
          this.$router.push({
            name: ROUTE_WORKFLOW_ADD,
            query: { claimGid: this.claimGid },
          });
          return;
        }
        vxm.breadcrumbs.setClaimName({ claimName: this.claimDetails.name });
      }
    }

    vxm.breadcrumbs.setProjectName({ projectName: this.claimDetails.project.name });
    vxm.breadcrumbs.setContractName({ contractName: this.claimDetails.contract.name });

    if (callback) {
      callback();
    }
  }

  private async loadSegmentDefinition() {
    if (this.claimGid) {
      const result = await executeApi(
        () => this.api.segmentInstances().getSegmentDefinition(this.claimGid),
        'Load Segment Definition',
      );
      if (result.success && result.data) {
        const definition = OtDataDrivenDefinition.createFromApiResponse(result.data.definition);
        const segmentConfig = SegmentConfig.createFromApiResponse(result.data.config);
        this.dataDrivenSegmentDefinition = definition;
        this.segmentConfig = segmentConfig;
      }
    }
  }

  private async loadSegmentResponse() {
    if (this.claimGid) {
      const result = await executeApi(
        () => this.api.segmentInstances().getSegmentInstanceResponse(this.claimGid),
        'Load Segment Responses',
      );
      if (result.success && result.data) {
        const instance = OtDataDrivenInstances.createFromApiResponse(result.data);
        this.dataDrivenSegmentInstances = instance;
      }
    }
  }

  private async loadSegmentEvaluation() {
    if (this.claimGid) {
      const result = await executeApi(
        () => this.api.segmentInstances().getSegmentInstanceEvaluation(this.claimGid),
        'Load Segment Evaluation',
      );
      if (result.success && result.data) {
        const evaluations = result.data.map(x => OtClientEvaluation.createFromApiResponse(x));
        this.evaluations = evaluations;
      }
    }
  }
  private dodgyHackToReloadCurrentView = 1;

  private async createNewDocument() {
    const params = new AddEditDocumentDialogParams({
      mode: AddEditDialogMode.CreateForWorkflow,
      targetOrganisationGid: this.claimDetails.project.organisationGid,
      context: 'Workflow',
    });
    const result = await vxm.modal.openAddEditDocumentDialog(params);
    if (result.ok && result.createdDocumentVersionGid) {
      // the document exists, but it's not associated with the workflow yet. Need to make another API call before we can reload the list.
      const request = new PostUpdateSegmentInstanceDocumentsRequestModel({
        associationsToAdd: [
          new PostUpdateSegmentInstanceDocumentsRequestDocumentVersionAssociationModel({
            associationType: SegmentInstanceDocumentVersionAssociationType.Supporting,
            gid: result.createdDocumentVersionGid,
          }),
        ],
        associationsToUpdate: [],
        associationsToRemove: [],
      });
      await executeApi(
        () => this.api.segmentInstances().postUpdateSegmentInstanceDocuments(this.claimGid, undefined, request),
        'Associate Document with Workflow',
      );
      // the documents tab does its own loading. We can't just call handleReloadDocuments here, because it doesn't exist
      // we could somehow pulse a signal down (update a Symbol) that could be watched, but meh
      // we could allow the child to register an onReloadDocuments handler by emitting a setReloadDocuments event, hold it, call it here, but meh
      // incrementing a variable to reload the whole shooting match works well enough.
      // Unfortunately it blows away the search criteria, that might be annoying.
      // If the user is sitting there with a list of 5 documents to create, all with a reference ABC123-something, and they search
      // for ABC-123 to see what needs creating
      // it might be beneficial to retain the search results. If that turns out to be a wanted feature, consider the setReloadDocuments event
      this.dodgyHackToReloadCurrentView = this.dodgyHackToReloadCurrentView + 1;
    }
  }

  private async importDocument() {
    const params = new SelectDocumentDialogParams({
      targetOrganisationGid: this.claimDetails.project.organisationGid,
      context: 'Workflow',
    });
    const dialogResult = await vxm.modal.openSelectDocumentDialog(params);
    if (dialogResult.ok) {
      const request = new PostUpdateSegmentInstanceDocumentsRequestModel({
        associationsToAdd: dialogResult.selectedDocuments.map(
          x =>
            new PostUpdateSegmentInstanceDocumentsRequestDocumentVersionAssociationModel({
              associationType: SegmentInstanceDocumentVersionAssociationType.Supporting,
              gid: x.documentVersionGid,
            }),
        ),
        associationsToUpdate: [],
        associationsToRemove: [],
      });
      await executeApi(
        () => this.api.segmentInstances().postUpdateSegmentInstanceDocuments(this.claimGid, undefined, request),
        'Associate Document with Workflow',
      );
      this.dodgyHackToReloadCurrentView = this.dodgyHackToReloadCurrentView + 1;
    }
  }
  private async importFromAconex() {
    const params = new AconexImportDialogParams({
      documentParams: new AddEditDocumentDialogParams({
        addToOrganisation: false,
        targetOrganisationGid: this.claimDetails.project.organisationGid,
        context: 'Workflow',
        mode: AddEditDialogMode.CreateForWorkflow,
      }),
    });
    const result = await doAconexImport(params);
    if (result.didImport && result.result?.createdDocumentVersionGid) {
      // the document exists, but it's not associated with the workflow yet. Need to make another API call before we can reload the list.
      const request = new PostUpdateSegmentInstanceDocumentsRequestModel({
        associationsToAdd: [
          new PostUpdateSegmentInstanceDocumentsRequestDocumentVersionAssociationModel({
            associationType: SegmentInstanceDocumentVersionAssociationType.Supporting,
            gid: result.result.createdDocumentVersionGid,
          }),
        ],
        associationsToUpdate: [],
        associationsToRemove: [],
      });
      await executeApi(
        () => this.api.segmentInstances().postUpdateSegmentInstanceDocuments(this.claimGid, undefined, request),
        'Associate Document with Workflow',
      );

      this.dodgyHackToReloadCurrentView = this.dodgyHackToReloadCurrentView + 1;
    }
  }

  // * LIFECYCLE
  private async created() {
    await this.checkRoutePath();
  }
}
